import { env } from './env.js';
import type { BrandGrantRole, InvitationApiError, WorkspaceInviteRole } from './invitations-api.js';

// T3 — dashboard fetch helpers for the workspace member-management
// endpoints. Sibling to invitations-api.

export type WorkspaceRole = 'owner' | 'admin' | 'member';
export type BrandRole = BrandGrantRole;

export interface MemberBrandAccess {
  brandId: string;
  brandName: string;
  brandSlug: string;
  role: BrandRole;
  implicit: boolean;
}

export interface OrgMemberSummary {
  userId: string;
  email: string;
  name: string;
  image: string | null;
  workspaceRole: WorkspaceRole;
  joinedAt: string;
  invitedByUserId: string | null;
  brandAccess: MemberBrandAccess[];
}

export interface BrandMemberSummary {
  userId: string;
  email: string;
  name: string;
  image: string | null;
  workspaceRole: WorkspaceRole;
  brandRole: BrandRole;
  implicit: boolean;
  addedAt: string | null;
  addedByUserId: string | null;
}

async function readJsonOrThrow<T>(res: Response, path: string): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  let body: { error?: string; message?: string } = {};
  try {
    body = await res.json();
  } catch {
    // Non-JSON error body.
  }
  const err = new Error(body.message ?? `${res.status} from ${path}`) as Error & {
    code?: string;
    status?: number;
  };
  err.code = body.error ?? `http_${res.status}`;
  err.status = res.status;
  throw err;
}

export async function fetchOrgMembers(): Promise<OrgMemberSummary[]> {
  const res = await fetch(`${env.API_URL}/api/team/members`, { credentials: 'include' });
  const body = await readJsonOrThrow<{ members: OrgMemberSummary[] }>(res, '/api/team/members');
  return body.members;
}

export async function fetchBrandMembers(brandId: string): Promise<BrandMemberSummary[]> {
  const res = await fetch(`${env.API_URL}/api/brands/${encodeURIComponent(brandId)}/members`, {
    credentials: 'include',
  });
  const body = await readJsonOrThrow<{ members: BrandMemberSummary[] }>(
    res,
    `/api/brands/${brandId}/members`,
  );
  return body.members;
}

export async function changeOrgMemberRole(userId: string, role: 'admin' | 'member'): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/team/members/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  await readJsonOrThrow<unknown>(res, `PATCH /api/team/members/${userId}`);
}

export async function removeOrgMember(userId: string): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/team/members/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await readJsonOrThrow<unknown>(res, `DELETE /api/team/members/${userId}`);
}

export async function addBrandMember(
  brandId: string,
  userId: string,
  role: BrandRole,
): Promise<void> {
  const res = await fetch(`${env.API_URL}/api/brands/${encodeURIComponent(brandId)}/members`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, role }),
  });
  await readJsonOrThrow<unknown>(res, `POST /api/brands/${brandId}/members`);
}

export async function changeBrandMemberRole(
  brandId: string,
  userId: string,
  role: BrandRole,
): Promise<void> {
  const res = await fetch(
    `${env.API_URL}/api/brands/${encodeURIComponent(brandId)}/members/${encodeURIComponent(userId)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    },
  );
  await readJsonOrThrow<unknown>(res, `PATCH /api/brands/${brandId}/members/${userId}`);
}

export async function removeBrandMember(brandId: string, userId: string): Promise<void> {
  const res = await fetch(
    `${env.API_URL}/api/brands/${encodeURIComponent(brandId)}/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  await readJsonOrThrow<unknown>(res, `DELETE /api/brands/${brandId}/members/${userId}`);
}

export type { InvitationApiError, WorkspaceInviteRole };
